import { HARDCODED_DATA_KEY } from "src/stores/models/data-key";
import { HARDCODED_TEMPLATE_KEY } from "@models";
import { QuestionTransformerConfig, TemplateConfig } from "@types";
import {
  captureSentryException,
  getTableColumns,
  markdownTableToObject,
  mergeTables,
  parseMarkdownTable,
  toMarkdownTable,
} from "@utils";
import Handlebars from "handlebars";
import moment from "moment";

export const dataKeyToQuestionTransformerConfig = (
  templatesConfig: TemplateConfig
): Record<string, QuestionTransformerConfig> => ({
  [HARDCODED_DATA_KEY.SOUP_DEPENDENCIES]: {
    inputs: ["software-items-table"],
    transformer: (inputs: Record<string, string>) => {
      const softwareItemsTable = markdownTableToObject(
        inputs["software-items-table"]
      );
      return softwareItemsTable
        .map((softwareItem: Record<string, string>) => {
          return `Software Item: ${softwareItem["Software Item"]}\nDependencies:\n`;
        })
        .join("\n");
    },
  },
  [HARDCODED_DATA_KEY.SOUP_LIST]: {
    inputs: ["soup-risk-levels", "soup-requirements"],
    transformer: (inputs: Record<string, string>) => {
      try {
        const table1 = parseMarkdownTable(inputs["soup-requirements"]);

        const table2 = parseMarkdownTable(inputs["soup-risk-levels"]);

        const mergedTable = mergeTables(table1, table2, "SOUP ID");
        const mergedMarkdown = toMarkdownTable(mergedTable);

        return mergedMarkdown;
      } catch (e) {
        captureSentryException(e);
        return "";
      }
    },
  },
  [HARDCODED_DATA_KEY.SYSTEM_TEST_REPORT]: {
    inputs: ["system-test-plan"],
    transformer: (inputs: Record<string, string>) => {
      try {
        // transform the table to a list of objects to be able to manipulate it
        const tableAsArray = parseMarkdownTable(inputs["system-test-plan"]);

        // create a new array to store the new table
        const systemTestReport = tableAsArray.map(
          (row: Record<string, string>) => ({
            "SR ID": row["SR ID"],
            "Test ID": row["Test ID"],
            Objective: row["Objective"],
            "Acceptance Criteria": row["Acceptance Criteria"],
            Acceptable: "",
            "Results if Unacceptable": "",
          })
        );

        const mergedMarkdown = toMarkdownTable(systemTestReport);

        return mergedMarkdown;
      } catch (e) {
        captureSentryException(e);
        return "";
      }
    },
  },
  [HARDCODED_DATA_KEY.BUG_TEST_RESULTS]: {
    inputs: ["bug-test"],
    transformer: (inputs: Record<string, string>) => {
      try {
        // transform the table to a list of objects to be able to manipulate it
        const tableAsArray = parseMarkdownTable(inputs["bug-test"]);

        // create a new array to store the new table
        const bugTestResults = tableAsArray.map(
          (row: Record<string, string>) => ({
            "SR ID": row["SR ID"],
            "Test ID": row["Test ID"],
            Objective: row["Objective"],
            "Test Steps": row["Test Steps"],
            "Acceptance Criteria": row["Acceptance Criteria"],
            "Acceptable (Yes/No)": "",
            Comments: "",
          })
        );

        const mergedMarkdown = toMarkdownTable(bugTestResults);

        return mergedMarkdown;
      } catch (e) {
        captureSentryException(e);
        return "";
      }
    },
  },
  [HARDCODED_DATA_KEY.VALIDATION_HISTORY]: {
    inputs: ["validation-software-version"],
    transformer: (inputs: Record<string, string>) => {
      return `
        | Validated Software Version        | Date             | Changes in Validation Process |
        | --------------------------------- | ---------------- | ----------------------------- |
        | ${inputs["validation-software-version"]} | ${moment().format("YYYY-MM-DD")} | None, initial validation  |`;
    },
  },
  [HARDCODED_DATA_KEY.RISK_HAZARDS]: {
    inputs: ["risk-analysis"],
    //Only list the risk that are "hazardous situations, hazard, harm"
    transformer: (inputs: Record<string, string>) => {
      try {
        // transform the table to a list of objects to be able to manipulate it
        const tableAsArray = parseMarkdownTable(inputs["risk-analysis"]);

        // create a new array to store the new table
        const riskTable = tableAsArray.map((row: Record<string, string>) => ({
          "Risk ID": row["Risk ID"],
          "Risk Type": row["Risk Type"],
          Hazard: row["Hazard"],
          "Hazardous Situation": row["Hazardous Situation"],
          Harm: row["Harm"],
        }));

        const mergedMarkdown = toMarkdownTable(riskTable);

        return mergedMarkdown;
      } catch (e) {
        captureSentryException(e);
        return "";
      }
    },
  },
  [HARDCODED_DATA_KEY.LITERATURE_SEARCH_RESULTS]: {
    inputs: [
      "literature-search-table",
      "pubmed-search-string",
      "cochrane-search-string",
    ],
    transformer: (inputs: Record<string, string>) => {
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        "literature-search-table",
        templatesConfig
      );

      const literatureSearchTableArray = parseMarkdownTable(
        inputs["literature-search-table"]
      ).filter((r) => keys.every((k) => r.hasOwnProperty(k)));

      const pubmedSearchTable = parseMarkdownTable(
        inputs["pubmed-search-string"]
      );

      const cochraneSearchTable = parseMarkdownTable(
        inputs["cochrane-search-string"]
      );

      const numberOfPubmedResults = pubmedSearchTable.length;
      const numberOfCochraneResults = cochraneSearchTable.length;
      const totalNumberOfResults = literatureSearchTableArray.length;
      const exclusions = literatureSearchTableArray.filter(
        (r) => r["Title / Abstract"]?.toLowerCase() === "excluded"
      ).length;
      const fullTextExclusions = literatureSearchTableArray.filter(
        (r) => r["Full-text"]?.toLowerCase() === "excluded"
      ).length;
      const includedPublications = literatureSearchTableArray.filter(
        (r) =>
          r["Title / Abstract"]?.toLowerCase() === "included" &&
          r["Full-text"]?.toLowerCase() === "included"
      ).length;

      // create a new array to store the new table
      return `
                            | Search Criteria                       | Results |
                            | ------------------------------------- | ------- |
                            | PubMed Total Results                  | ${numberOfPubmedResults}    |
                            | Cochrane Total Results                | ${numberOfCochraneResults}    |
                            | Total Number of Results               | ${totalNumberOfResults}    |
                            | Number of Title / Abstract Exclusions | ${exclusions}    |
                            | Number of Full-Text Exclusions        | ${fullTextExclusions}    |
                            | Number of Included Publications       | ${includedPublications}    |
                          `;
    },
  },
  [HARDCODED_DATA_KEY.PERFORMANCE_LITERATURE_DEVICE]: {
    inputs: [
      "literature-search-table",
      "pubmed-search-string",
      "cochrane-search-string",
    ],
    transformer: (inputs: Record<string, string>) => {
      // transform the table to a list of objects to be able to manipulate it
      const literatureSearchTableArray = parseMarkdownTable(
        inputs["literature-search-table"]
      );

      const pubmedSearchTable = parseMarkdownTable(
        inputs["pubmed-search-string"]
      );

      const cochraneSearchTable = parseMarkdownTable(
        inputs["cochrane-search-string"]
      );

      const searchTables = pubmedSearchTable.concat(cochraneSearchTable);

      // get the table columns from the config
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        "literature-search-table",
        templatesConfig
      );

      const literatureSearchTableMapped = literatureSearchTableArray
        // Make sure all the keys are present in the object to prevent undefined object properties
        .filter((r) => keys.every((k) => r.hasOwnProperty(k)))
        .filter(
          (r) =>
            r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
              "performance & safety" &&
            r["Device / State of the art"]?.toLowerCase()?.trim() ===
              "device" &&
            r["Full-text"]?.toLowerCase()?.trim() === "included" &&
            r["Title / Abstract"]?.toLowerCase()?.trim() === "included"
        )
        .map(
          (r) =>
            `| ${r["PubID"]} | ${r["Title"]} | ${
              searchTables.find(
                (item) => item["ID"]?.trim() === r["PubID"]?.trim()
              )?.["Abstract"]
            } |`
        )
        .join("\n");

      return `| PubID | Title | Summary |\n| ---- | ---- | ---- |\n${literatureSearchTableMapped}`;
    },
  },
  [HARDCODED_DATA_KEY.PERFORMANCE_LITERATURE_STATE_OF_THE_ART]: {
    inputs: [
      "literature-search-table",
      "pubmed-search-string",
      "cochrane-search-string",
    ],
    transformer: (inputs: Record<string, string>) => {
      // transform the table to a list of objects to be able to manipulate it
      const literatureSearchTableArray = parseMarkdownTable(
        inputs["literature-search-table"]
      );

      const pubmedSearchTable = parseMarkdownTable(
        inputs["pubmed-search-string"]
      );

      const cochraneSearchTable = parseMarkdownTable(
        inputs["cochrane-search-string"]
      );

      const searchTables = pubmedSearchTable.concat(cochraneSearchTable);

      // get the table columns from the config
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        "literature-search-table",
        templatesConfig
      );

      const literatureSearchTableMapped = literatureSearchTableArray
        // Make sure all the keys are present in the object to prevent undefined object properties
        .filter((r) => keys.every((k) => r.hasOwnProperty(k)))
        .filter(
          (r) =>
            r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
              "performance & safety" &&
            r["Device / State of the art"]?.toLowerCase()?.trim() ===
              "state of the art" &&
            r["Full-text"]?.toLowerCase()?.trim() === "included" &&
            r["Title / Abstract"]?.toLowerCase()?.trim() === "included"
        )
        .map(
          (r) =>
            `| ${r["PubID"]} | ${r["Title"]} | ${
              searchTables.find(
                (item) => item["ID"]?.trim() === r["PubID"]?.trim()
              )?.["Abstract"]
            } |`
        )
        .join("\n");

      return `| PubID | Title | Summary |\n| ---- | ---- | ---- |\n${literatureSearchTableMapped}`;
    },
  },
  [HARDCODED_DATA_KEY.SAFETY_LITERATURE_DEVICE]: {
    inputs: [
      "literature-search-table",
      "pubmed-search-string",
      "cochrane-search-string",
    ],
    transformer: (inputs: Record<string, string>) => {
      // transform the table to a list of objects to be able to manipulate it
      const literatureSearchTableArray = parseMarkdownTable(
        inputs["literature-search-table"]
      );

      const pubmedSearchTable = parseMarkdownTable(
        inputs["pubmed-search-string"]
      );

      const cochraneSearchTable = parseMarkdownTable(
        inputs["cochrane-search-string"]
      );

      const searchTables = pubmedSearchTable.concat(cochraneSearchTable);

      // get the table columns from the config
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        "literature-search-table",
        templatesConfig
      );

      const literatureSearchTableMapped = literatureSearchTableArray
        // Make sure all the keys are present in the object to prevent undefined object properties
        .filter((r) => keys.every((k) => r.hasOwnProperty(k)))
        .filter(
          (r) =>
            (r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
              "safety" ||
              r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
                "performance & safety") &&
            r["Device / State of the art"]?.toLowerCase()?.trim() ===
              "device" &&
            r["Full-text"]?.toLowerCase()?.trim() === "included" &&
            r["Title / Abstract"]?.toLowerCase()?.trim() === "included"
        )
        .map(
          (r) =>
            `| ${r["PubID"]} | ${r["Title"]} | ${
              searchTables.find(
                (item) => item["ID"]?.trim() === r["PubID"]?.trim()
              )?.["Abstract"]
            } |`
        )
        .join("\n");

      return `| PubID | Title | Summary |\n| ---- | ---- | ---- |\n${literatureSearchTableMapped}`;
    },
  },
  [HARDCODED_DATA_KEY.SAFETY_LITERATURE_STATE_OF_THE_ART]: {
    inputs: [
      "literature-search-table",
      "pubmed-search-string",
      "cochrane-search-string",
    ],
    transformer: (inputs: Record<string, string>) => {
      // transform the table to a list of objects to be able to manipulate it
      const literatureSearchTableArray = parseMarkdownTable(
        inputs["literature-search-table"]
      );

      const pubmedSearchTable = parseMarkdownTable(
        inputs["pubmed-search-string"]
      );

      const cochraneSearchTable = parseMarkdownTable(
        inputs["cochrane-search-string"]
      );

      const searchTables = pubmedSearchTable.concat(cochraneSearchTable);

      // get the table columns from the config
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        "literature-search-table",
        templatesConfig
      );

      const literatureSearchTableMapped = literatureSearchTableArray
        // Make sure all the keys are present in the object to prevent undefined object properties
        .filter((r) => keys.every((k) => r.hasOwnProperty(k)))
        .filter(
          (r) =>
            (r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
              "safety" ||
              r["Performance & Safety / Safety"]?.toLowerCase()?.trim() ===
                "performance & safety") &&
            r["Device / State of the art"]?.toLowerCase()?.trim() ===
              "state of the art" &&
            r["Full-text"]?.toLowerCase()?.trim() === "included" &&
            r["Title / Abstract"]?.toLowerCase()?.trim() === "included"
        )
        .map(
          (r) =>
            `| ${r["PubID"]} | ${r["Title"]} | ${
              searchTables.find(
                (item) => item["ID"]?.trim() === r["PubID"]?.trim()
              )?.["Abstract"]
            } |`
        )
        .join("\n");

      return `| PubID | Title | Summary |\n| ---- | ---- | ---- |\n${literatureSearchTableMapped}`;
    },
  },
  [HARDCODED_DATA_KEY.CLINICAL_TRIALS_SEARCH_RESULTS]: {
    inputs: [HARDCODED_DATA_KEY.CLINICAL_TRIAL_SEARCH_TABLE],
    transformer: (inputs: Record<string, string>) => {
      // get the table columns from the config
      const keys = getTableColumns(
        HARDCODED_TEMPLATE_KEY.CLINICAL_EVALUATION_REPORT,
        HARDCODED_DATA_KEY.CLINICAL_TRIAL_SEARCH_TABLE,
        templatesConfig
      );

      const clinicalTrialsTableArray = parseMarkdownTable(
        inputs[HARDCODED_DATA_KEY.CLINICAL_TRIAL_SEARCH_TABLE]
      ).filter((r) => keys.every((k) => r.hasOwnProperty(k)));

      const totalNumberOfResults = clinicalTrialsTableArray.length;
      const numberOfRelevantResults = clinicalTrialsTableArray.filter(
        (r) => r["Relevant"]?.toLowerCase()?.trim() === "relevant"
      ).length;

      return `
                        | Search Criteria                       | Results |
                        | ------------------------------------- | ------- |
                        | Total Number of Results               | ${totalNumberOfResults}    |
                        | Number of Trials with Results Relevant to Device | ${numberOfRelevantResults}    |
                       `;
    },
  },
  [HARDCODED_DATA_KEY.FSN_DESCRIPTION]: {
    inputs: ["clinical-device-description"],
    transformer: (inputs: Record<string, string>) => {
      return inputs["clinical-device-description"] || "";
    },
  },
  [HARDCODED_DATA_KEY.RELEASE_DOCUMENTS]: {
    inputs: ["management-team"],
    transformer: (
      _: Record<string, string>,
      docReferences: Record<string, string>
    ) => {
      const table = `
                      | Document Name                 | Document Number                         | Updated | Justification for no update |
                      | ----------------------------- | --------------------------------------- | ------- | --------------------------- |
                      | User Needs List               | {{ did-user-needs-list }}               |         |                             |
                      | Intended Use                  | {{ did-intended-use }}                  |         |                             |
                      | Risk Management Plan          | {{ did-risk-management-plan }}          |         |                             |
                      | Risk Assessment               | {{ did-risk-assessment }}               |         |                             |
                      | Risk Management Report        | {{ did-risk-management-report }}        |         |                             |
                      | System Requirements List    | {{ did-system-requirements-list }}    |         |                             |
                      | Subsystem Requirements List    | {{ did-subsystem-requirements-list }}    |         |                             |
                      | Software Architecture         | {{ did-software-architecture }}         |         |                             |
                      | Software System Test Plan     | {{ did-software-system-test-plan }}     |         |                             |
                      | Software System Test Report   | {{ did-software-system-test-report}}    |         |                             |
                      | Usability Evaluation Plan     | {{ did-usability-evaluation-plan }}     |         |                             |
                      | Usability Evaluation Protocol | {{ did-usability-evaluation-protocol }} |         |                             |
                      | Usability Evaluation Report   | {{ did-usability-evaluation-report }}   |         |                             |
                      | Clinical Evaluation Plan      | {{ did-clinical-evaluation-plan }}      |         |                             |
                      | Clinical Evaluation Report    | {{ did-clinical-evaluation-report }}    |         |                             |
                      | Instructions for Use          | {{ did-instructions-for-use }}          |         |                             |
                      | Release Notes                 | {{ did-release-notes }}                 |         |                             |
                      | Device Label                  | {{ did-device-label }}                  |         |                             |
                      | Declaration of Conformity     | {{ did-eu-declaration-of-conformity }}     |         |                             |
                      | Technical File                | {{ did-technical-file }}                |         |                             |`;
      // | Change Request                | {{ did-change-request}}                 |         |                             |
      // | Change Evaluation Report      | {{ did-change-evaluation-report }}      |         |                             |`;

      const template = Handlebars.compile(table, { noEscape: true });
      return template(docReferences);
    },
  },
  [HARDCODED_DATA_KEY.MANAGEMENT_REVIEW_SIGNATURES]: {
    inputs: ["management-team"],
    transformer: (inputs: Record<string, string>) => {
      const managementTeam = parseMarkdownTable(inputs["management-team"]);
      return `
        | Position | Name | Signature |
        | -------- | ---- | --------- |
        ${managementTeam.map((member: Record<string, string>) => `| ${member.Position} | ${member.Name} | |`).join("\n")}
                      `;
    },
  },
  [HARDCODED_DATA_KEY.CHANGE_SOFTWARE_ITEMS]: {
    inputs: ["software-items-table"],
    transformer: (inputs: Record<string, string>) => {
      const softwareItems = parseMarkdownTable(inputs["software-items-table"]);
      return `
      | Software Item | Affected by Change (Yes/No) | Comments |
      | ------------- | --------------------------- | -------- |
      ${softwareItems.map((item: Record<string, string>) => `| ${item["Software Item"]} | | |`).join("\n")}
                    `;
    },
  },
});
